import { useState } from 'react';
import type { Control } from 'react-hook-form';

import { Select } from '@/components/common/select';
import { FormField } from '@/components/forms/form-field';
import { useCursorSelect } from '@/hooks/use-cursor-select';

import { listUsCities } from '../api';
import { dealerKeys } from '../hooks';
import type { DealerFormValues, UsCity } from '../types';

const toValue = (c: { city: string; state: string }) => `${c.city}|${c.state}`;
const toOption = (c: UsCity) => ({ value: toValue(c), label: c.label });

/** 118643 City: async, server-searched, selection-only single-select holding `{ city, state }`.
 *  Options appear from the 2nd character (`listUsCities` answers shorter terms locally, empty);
 *  under 2 characters the popup shows nothing rather than "No matching cities". */
export function CitySelectField({ control }: { control: Control<DealerFormValues> }) {
  const [term, setTerm] = useState('');
  const cursor = useCursorSelect({
    queryKey: dealerKeys.usCities,
    fetchPage: listUsCities,
    toOption,
  });
  const searchable = term.trim().length >= 2;

  return (
    <FormField name="city" control={control} label="City" required>
      {({ field, fieldState }) => {
        const picked = field.value;
        const value = picked ? toValue(picked) : undefined;
        // The picked city stays drawable after its search term is cleared (and as an Edit prefill).
        const options =
          picked && value && !cursor.options.some((o) => o.value === value)
            ? [{ value, label: `${picked.city}, ${picked.state}` }, ...cursor.options]
            : cursor.options;
        return (
          <Select
            id="city"
            style={{ width: '100%' }}
            placeholder="Select"
            status={fieldState.error ? 'error' : undefined}
            value={value}
            options={options}
            showSearch={{
              ...cursor.showSearch,
              onSearch: (next: string) => {
                setTerm(next);
                cursor.showSearch.onSearch(next);
              },
            }}
            loading={cursor.loading}
            onPopupScroll={cursor.onPopupScroll}
            emptyText={searchable ? 'No matching cities' : undefined}
            notFoundContent={searchable ? undefined : null}
            onOpenChange={(open) => {
              if (!open) {
                cursor.resetSearch();
                setTerm('');
              }
            }}
            onChange={(next) => {
              const [city, state] = String(next).split('|');
              field.onChange(city && state ? { city, state } : null);
            }}
            onBlur={field.onBlur}
            ref={field.ref}
          />
        );
      }}
    </FormField>
  );
}
